import type { components } from "@/api/types";

export type ReportType = components["schemas"]["ReportType"];

// Canonical label map for every report type — drives `reportTypeLabel`, so
// historical/typed rows (e.g. an "exposure" report) still render a human label.
export const REPORT_TYPES: { value: ReportType; label: string }[] = [
  { value: "incident_summary", label: "Incident Summary" },
  { value: "exposure", label: "Exposure" },
  { value: "voice_record", label: "Voice Record" },
  { value: "narrative", label: "Narrative" },
];

// Report types offered as selector buttons under the incident Report tab.
// "exposure" is intentionally excluded — the incident detail page already has a
// dedicated "Exposure" tab, so surfacing it again here would be redundant.
export const SELECTABLE_REPORT_TYPES = REPORT_TYPES.filter(
  (t) => t.value !== "exposure",
);

export function reportTypeLabel(type: ReportType): string {
  return REPORT_TYPES.find((t) => t.value === type)?.label ?? type;
}

/**
 * Defence-in-depth guard for presigned report URLs before they reach a DOM sink
 * (`<a href>`, `<iframe src>`, programmatic navigation). A legitimate S3 presigned
 * URL is always `https:`; rejecting any other scheme means a compromised or buggy
 * API response can't smuggle a `javascript:`/`data:` URL into a click-through XSS.
 * Returns the original string when safe, otherwise `null`.
 */
export function safeReportUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

/** Whole-second duration as "Hh Mm" / "Mm Ss" / "Ss". */
export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return "—";
  }
  if (seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/**
 * The report sections come back as untyped `{[key: string]: unknown}` maps
 * keyed by a `heading` field; pull one out by heading for typed rendering.
 */
export function findSection(
  sections: { [key: string]: unknown }[],
  heading: string,
): Record<string, unknown> | undefined {
  return sections.find((s) => s.heading === heading);
}

/** Read an array-valued key off a section, defaulting to `[]`. */
export function sectionRows<T>(
  section: Record<string, unknown> | undefined,
  key: string,
): T[] {
  const value = section?.[key];
  return Array.isArray(value) ? (value as T[]) : [];
}
